class ComparisonChart extends HTMLElement {
  connectedCallback() {
    this.cells = Array.from(this.querySelectorAll('.comparison-chart__cell--value'));
    this.panels = Array.from(this.querySelectorAll('.comparison-chart__panel'));
    this.mobileRowSyncBreakpoint = 390;
    this.floatStartDelayMs = 500;
    this.syncRafId = null;
    this.cellToImageWrap = new WeakMap();
    this.cellToColumn = new WeakMap();
    this.imageWrapStopTimers = new WeakMap();
    this.imageWrapStartDelayTimers = new WeakMap();
    this.imageWrapStartTimes = new WeakMap();
    this.imageWrapRestartRequested = new WeakMap();
    this.imageWrapToColumn = new WeakMap();
    this.imageWraps = new Set();

    this.handleCellEnter = this.handleCellEnter.bind(this);
    this.handleCellLeave = this.handleCellLeave.bind(this);
    this.handleImageWrapEnter = this.handleImageWrapEnter.bind(this);
    this.handleImageWrapLeave = this.handleImageWrapLeave.bind(this);
    this.handleImageWrapIteration = this.handleImageWrapIteration.bind(this);
    this.handleResize = this.handleResize.bind(this);

    this.cells.forEach((cell) => {
      const column = cell.closest('.comparison-chart__column');
      const imageWrap = column?.querySelector('.comparison-chart__image-wrap');

      if (column instanceof HTMLElement && imageWrap instanceof HTMLElement) {
        this.cellToColumn.set(cell, column);
        this.cellToImageWrap.set(cell, imageWrap);
        this.imageWrapToColumn.set(imageWrap, column);

        if (!this.imageWraps.has(imageWrap)) {
          this.imageWraps.add(imageWrap);
          imageWrap.addEventListener('animationiteration', this.handleImageWrapIteration);
          imageWrap.addEventListener('mouseenter', this.handleImageWrapEnter);
          imageWrap.addEventListener('mouseleave', this.handleImageWrapLeave);
          imageWrap.addEventListener('focusin', this.handleImageWrapEnter);
          imageWrap.addEventListener('focusout', this.handleImageWrapLeave);
        }
      }

      cell.addEventListener('mouseenter', this.handleCellEnter);
      cell.addEventListener('mouseleave', this.handleCellLeave);
      cell.addEventListener('focusin', this.handleCellEnter);
      cell.addEventListener('focusout', this.handleCellLeave);
    });

    window.addEventListener('resize', this.handleResize, { passive: true });
    this.syncRowHeights();

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => this.syncRowHeights());
    }
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.handleResize);

    if (this.syncRafId) {
      window.cancelAnimationFrame(this.syncRafId);
      this.syncRafId = null;
    }

    this.cells.forEach((cell) => {
      cell.removeEventListener('mouseenter', this.handleCellEnter);
      cell.removeEventListener('mouseleave', this.handleCellLeave);
      cell.removeEventListener('focusin', this.handleCellEnter);
      cell.removeEventListener('focusout', this.handleCellLeave);

      const imageWrap = this.cellToImageWrap.get(cell);
      if (imageWrap instanceof HTMLElement) {
        const stopTimer = this.imageWrapStopTimers.get(imageWrap);
        if (stopTimer) {
          window.clearTimeout(stopTimer);
        }

        const startDelayTimer = this.imageWrapStartDelayTimers.get(imageWrap);
        if (startDelayTimer) {
          window.clearTimeout(startDelayTimer);
        }

        imageWrap.classList.remove('is-floating');
        imageWrap.style.transform = '';
      }
    });

    this.imageWraps.forEach((imageWrap) => {
      imageWrap.removeEventListener('animationiteration', this.handleImageWrapIteration);
      imageWrap.removeEventListener('mouseenter', this.handleImageWrapEnter);
      imageWrap.removeEventListener('mouseleave', this.handleImageWrapLeave);
      imageWrap.removeEventListener('focusin', this.handleImageWrapEnter);
      imageWrap.removeEventListener('focusout', this.handleImageWrapLeave);
    });

    this.clearRowHeightOverrides();
  }

  handleResize() {
    if (this.syncRafId) {
      window.cancelAnimationFrame(this.syncRafId);
    }

    this.syncRafId = window.requestAnimationFrame(() => {
      this.syncRafId = null;
      this.syncRowHeights();
    });
  }

  clearRowHeightOverrides() {
    this.panels.forEach((panel) => {
      const rowCells = panel.querySelectorAll('.comparison-chart__column-rows > .comparison-chart__cell');
      rowCells.forEach((cell) => {
        if (cell instanceof HTMLElement) {
          cell.style.minHeight = '';
        }
      });
    });
  }

  syncRowHeights() {
    if (window.innerWidth > this.mobileRowSyncBreakpoint) {
      this.clearRowHeightOverrides();
      return;
    }

    this.panels.forEach((panel) => {
      const columns = Array.from(panel.querySelectorAll('.comparison-chart__column-rows')).map((rows) => {
        return Array.from(rows.children).filter(
          (cell) => cell instanceof HTMLElement && cell.classList.contains('comparison-chart__cell')
        );
      });

      const rowCount = columns.reduce((max, column) => Math.max(max, column.length), 0);

      for (let rowIndex = 0; rowIndex < rowCount; rowIndex += 1) {
        let maxRowHeight = 0;

        columns.forEach((column) => {
          const cell = column[rowIndex];
          if (cell instanceof HTMLElement) {
            cell.style.minHeight = '';
          }
        });

        columns.forEach((column) => {
          const cell = column[rowIndex];
          if (cell instanceof HTMLElement) {
            maxRowHeight = Math.max(maxRowHeight, cell.getBoundingClientRect().height);
          }
        });

        if (maxRowHeight > 0) {
          const normalizedHeight = `${Math.ceil(maxRowHeight)}px`;
          columns.forEach((column) => {
            const cell = column[rowIndex];
            if (cell instanceof HTMLElement) {
              cell.style.minHeight = normalizedHeight;
            }
          });
        }
      }
    });
  }

  handleCellEnter(event) {
    const cell = event.currentTarget;
    if (!(cell instanceof HTMLElement)) {
      return;
    }

    const marker = cell.querySelector('.comparison-chart__marker');
    if (marker instanceof HTMLElement) {
      marker.classList.remove('is-pulsing');
      void marker.offsetWidth;
      marker.classList.add('is-pulsing');

      const handlePulseEnd = (pulseEvent) => {
        if (pulseEvent.target === marker) {
          marker.classList.remove('is-pulsing');
        }
      };

      marker.addEventListener('animationend', handlePulseEnd, { once: true });
    }

    const column = this.cellToColumn.get(cell);
    const imageWrap = this.cellToImageWrap.get(cell);
    if (!(imageWrap instanceof HTMLElement)) {
      return;
    }

    if (imageWrap.classList.contains('is-floating')) {
      this.imageWrapRestartRequested.set(imageWrap, true);
      return;
    }

    if (!(column instanceof HTMLElement)) {
      return;
    }

    this.queueFloatStart(imageWrap, column);
  }

  handleCellLeave(event) {
    const cell = event.currentTarget;
    if (!(cell instanceof HTMLElement)) {
      return;
    }

    const column = this.cellToColumn.get(cell);
    const imageWrap = this.cellToImageWrap.get(cell);
    if (!(column instanceof HTMLElement) || !(imageWrap instanceof HTMLElement)) {
      return;
    }

    this.cancelStartDelay(imageWrap);
    this.scheduleStopAtCycleEnd(imageWrap, column);
  }

  /** @param {MouseEvent|FocusEvent} event */
  handleImageWrapEnter(event) {
    const imageWrap = event.currentTarget;
    if (!(imageWrap instanceof HTMLElement)) {
      return;
    }

    const column = this.imageWrapToColumn.get(imageWrap);
    if (!(column instanceof HTMLElement)) {
      return;
    }

    this.imageWrapRestartRequested.set(imageWrap, false);
    this.queueFloatStart(imageWrap, column);
  }

  /** @param {MouseEvent|FocusEvent} event */
  handleImageWrapLeave(event) {
    const imageWrap = event.currentTarget;
    if (!(imageWrap instanceof HTMLElement)) {
      return;
    }

    const column = this.imageWrapToColumn.get(imageWrap);
    if (!(column instanceof HTMLElement)) {
      return;
    }

    this.cancelStartDelay(imageWrap);
    this.scheduleStopAtCycleEnd(imageWrap, column);
  }

  /** @param {HTMLElement} imageWrap */
  cancelStartDelay(imageWrap) {
    const existingStartDelayTimer = this.imageWrapStartDelayTimers.get(imageWrap);
    if (existingStartDelayTimer) {
      window.clearTimeout(existingStartDelayTimer);
      this.imageWrapStartDelayTimers.delete(imageWrap);
    }
  }

  /** @param {HTMLElement} imageWrap @param {HTMLElement} column */
  queueFloatStart(imageWrap, column) {
    const existingStopTimer = this.imageWrapStopTimers.get(imageWrap);
    if (existingStopTimer) {
      window.clearTimeout(existingStopTimer);
      this.imageWrapStopTimers.delete(imageWrap);
    }

    this.cancelStartDelay(imageWrap);

    const startDelayTimer = window.setTimeout(() => {
      if (!this.columnHasActiveCell(column) || imageWrap.classList.contains('is-floating')) {
        return;
      }

      this.startFloating(imageWrap);
      this.imageWrapStartDelayTimers.delete(imageWrap);
    }, this.floatStartDelayMs);

    this.imageWrapStartDelayTimers.set(imageWrap, startDelayTimer);
  }

  /** @param {HTMLElement} imageWrap @param {HTMLElement} column */
  scheduleStopAtCycleEnd(imageWrap, column) {
    const existingStopTimer = this.imageWrapStopTimers.get(imageWrap);
    if (existingStopTimer) {
      window.clearTimeout(existingStopTimer);
    }

    const durationMs = this.getAnimationDurationMs(imageWrap) || 3000;
    const startedAt = this.imageWrapStartTimes.get(imageWrap) || performance.now();
    const elapsed = Math.max(0, performance.now() - startedAt);
    const remainder = durationMs - (elapsed % durationMs);

    const timer = window.setTimeout(() => {
      const stillActiveInColumn = this.columnHasActiveCell(column);

      if (!stillActiveInColumn) {
        imageWrap.classList.remove('is-floating');
        imageWrap.style.transform = '';
        this.imageWrapStartTimes.delete(imageWrap);
        this.imageWrapRestartRequested.delete(imageWrap);
      }
    }, remainder);

    this.imageWrapStopTimers.set(imageWrap, timer);
  }

  /** @param {AnimationEvent} event */
  handleImageWrapIteration(event) {
    const imageWrap = event.currentTarget;
    if (!(imageWrap instanceof HTMLElement) || !imageWrap.classList.contains('is-floating')) {
      return;
    }

    const restartRequested = this.imageWrapRestartRequested.get(imageWrap);
    if (!restartRequested) {
      return;
    }

    const column = this.imageWrapToColumn.get(imageWrap);
    if (!(column instanceof HTMLElement)) {
      this.imageWrapRestartRequested.delete(imageWrap);
      return;
    }

    if (!this.columnHasActiveCell(column)) {
      this.imageWrapRestartRequested.delete(imageWrap);
      return;
    }

    this.startFloating(imageWrap);
  }

  /** @param {HTMLElement} column */
  columnHasActiveCell(column) {
    return column.matches(':hover') || column.matches(':focus-within');
  }

  /** @param {HTMLElement} imageWrap */
  startFloating(imageWrap) {
    imageWrap.classList.remove('is-floating');
    void imageWrap.offsetWidth;
    imageWrap.classList.add('is-floating');
    this.imageWrapStartTimes.set(imageWrap, performance.now());
    this.imageWrapRestartRequested.set(imageWrap, false);
  }

  /** @param {HTMLElement} element */
  getAnimationDurationMs(element) {
    const durationValue = window.getComputedStyle(element).animationDuration || '';
    const firstDuration = durationValue.split(',')[0]?.trim();

    if (!firstDuration || firstDuration === '0s') {
      return 0;
    }

    if (firstDuration.endsWith('ms')) {
      const ms = parseFloat(firstDuration.replace('ms', ''));
      return Number.isFinite(ms) ? ms : 0;
    }

    if (firstDuration.endsWith('s')) {
      const seconds = parseFloat(firstDuration.replace('s', ''));
      return Number.isFinite(seconds) ? seconds * 1000 : 0;
    }

    return 0;
  }
}

if (!customElements.get('comparison-chart')) {
  customElements.define('comparison-chart', ComparisonChart);
}
